import { useCallback, useEffect, useState } from 'react';
import {
  Anchor,
  Button,
  Card,
  Grid,
  Group,
  List,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  DEFAULT_NEWSLETTER_CONSENT_TEXT,
  DEFAULT_NEWSLETTER_OPT_IN,
  type NewsletterOptIn,
  type ThemeOverrides,
} from '@quizee/core';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, errorMessage, type ConfigPatch } from '../api/client';
import type { BrandDetail, OrgSettingsView } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import {
  consentChoiceOf,
  consentPatchValue,
  domainAtFault,
  inheritedNewsletterConsent,
  inheritedOptIn,
  isEmptyTheme,
  readDomain,
  themeProblems,
  tidiedDifference,
  type ConsentChoice,
  type Layer,
} from '../brand/inheritance';
import { asSettings, resolvePreview } from '../brand/preview';
import { ConsentRead, ConsentSection, SavedDifference } from '../components/ConsentSection';
import { DomainsField } from '../components/DomainsField';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { NewsletterRead, NewsletterSection } from '../components/NewsletterSection';
import { ReadOnlyNote } from '../components/OverrideField';
import { ReadEdit } from '../components/ReadEdit';
import { ReadList, ReadRow } from '../components/ReadValue';
import { ThemeRead, ThemeSection } from '../components/ThemeSection';
import { PreviewCaption, WidgetPreview } from '../components/WidgetPreview';

/**
 * One masthead: its name, where it publishes, and the settings it can take from the publisher or
 * answer for itself.
 *
 * The publisher's own settings are loaded alongside the brand, not because the brand's resolved
 * values need them, the API has already worked those out, but because an inherited field has to be
 * able to say where it came from. "Inherited" on its own sends an editor looking for a screen
 * nobody has named.
 *
 * Four sections, each read first and edited on its own. Editing is per section rather than per
 * screen because these four are looked at for unrelated reasons: somebody adding a staging domain
 * is not thinking about the consent notice, and putting them all into one form would mean every
 * visit here offered to change all four. They share the record they live in but not a save: the
 * API applies the fields a body names and leaves the rest alone, so a section sends its own fields
 * and stays silent about the other three. Two people on the same brand can then change different
 * sections without the later save quietly putting back what the earlier one changed. That is also
 * why a draft is never lifted out of a section: an unsaved theme cannot ride along with a saved
 * domain if nothing outside the theme section can see it.
 */
export function BrandSettings() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';
  const navigate = useNavigate();
  const { brandId } = useParams<{ brandId: string }>();

  const [detail, setDetail] = useState<BrandDetail | null>(null);
  const [orgView, setOrgView] = useState<OrgSettingsView | null>(null);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);

  const [problem, setProblem] = useState<string | null>(null);
  const [domainProblem, setDomainProblem] = useState<{ index: number; message: string } | null>(
    null,
  );
  const [savedDifference, setSavedDifference] = useState<string | null>(null);
  const [savedNewsletterDifference, setSavedNewsletterDifference] = useState<string | null>(null);
  // After a save has been refused, every domain problem is shown whether or not its box was left.
  const [reveal, setReveal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleteProblem, setDeleteProblem] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // What the preview draws while a section is open. Null means it draws what is saved.
  const [themeDraft, setThemeDraft] = useState<ThemeOverrides | null>(null);
  const [consentDraft, setConsentDraft] = useState<ConsentDraft | null>(null);

  const load = useCallback(
    (quiet = false) => {
      if (orgId === null || brandId === undefined) return;
      setLoadProblem(null);
      if (!quiet) setDetail(null);
      Promise.all([api.brand(orgId, brandId), api.orgSettings(orgId)])
        .then(([brand, settings]) => {
          setDetail(brand);
          setOrgView(settings);
        })
        .catch((error: unknown) => {
          if (!quiet) setLoadProblem(errorMessage(error));
        });
    },
    [brandId, orgId],
  );

  useEffect(() => load(), [load]);

  /**
   * Send the fields the section being saved owns, and nothing else.
   *
   * Three states have to survive the trip. A field the body does not name is left exactly as it
   * is, which is what keeps one section's save off another section's values. Null is a value in
   * its own right: it is how a section says "stop deciding this here", and it puts the field back
   * to whatever the publisher's defaults hold.
   */
  async function saveWith(changed: BrandPatch): Promise<boolean> {
    if (orgId === null || brandId === undefined) return false;
    setProblem(null);
    setDomainProblem(null);
    setSavedDifference(null);
    setSavedNewsletterDifference(null);

    try {
      const saved = await api.updateBrand(orgId, brandId, changed);
      setSavedDifference(tidiedDifference(changed.consent_text, saved.brand.consent_text));
      setSavedNewsletterDifference(
        tidiedDifference(changed.newsletter_consent_text, saved.brand.newsletter_consent_text),
      );
      notifications.show({ message: 'Brand saved.', color: 'violet' });
      // Reloaded rather than patched in: changing the notice is what makes live interactions fall
      // behind it, so the list of what needs republishing is only right after asking again.
      load(true);
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'invalid_domain') {
        // Only a save that named the domains can be refused for one of them.
        const entries = changed.domains ?? [];
        const at = domainAtFault(error.message, entries);
        if (at >= 0) setDomainProblem({ index: at, message: error.message });
        else setProblem(error.message);
      } else {
        setProblem(errorMessage(error));
      }
      return false;
    }
  }

  if (loadProblem !== null) {
    return (
      <>
        <BackLink />
        <ErrorNote message={loadProblem} onRetry={() => load()} />
      </>
    );
  }

  if (detail === null || orgView === null) return <Loading label="Loading this brand" />;

  const brand = detail;
  const below: readonly Layer[] = [{ label: orgName, settings: orgView.settings }];
  const inheritedMode = inheritedOptIn(
    below,
    orgView.system_defaults.newsletter_opt_in ?? DEFAULT_NEWSLETTER_OPT_IN,
  );
  // An API that does not answer with this is an API that never let anybody set it, so what such a
  // publisher resolves to really is the quizee default rather than a value we failed to read.
  const newsletterSystem =
    orgView.system_defaults.newsletter_consent_text ?? DEFAULT_NEWSLETTER_CONSENT_TEXT;
  const newsletterInherited = inheritedNewsletterConsent(below, newsletterSystem);

  // The preview follows whichever section is open, and what is saved when none of them is.
  const themeShown = themeDraft ?? brand.theme ?? {};
  const consentShown =
    consentDraft === null
      ? brand.consent_text
      : consentPatchValue(consentDraft.choice, consentDraft.text);
  const resolved = resolvePreview(
    asSettings(orgView.settings),
    {
      ...(isEmptyTheme(themeShown) ? {} : { theme: themeShown }),
      ...(consentShown === null ? {} : { consent_text: consentShown }),
    },
    brand.resolved,
  );

  return (
    <>
      <BackLink />
      <Title order={1} size="h2">
        {brand.name}
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        Anything this brand does not set for itself comes from the defaults for {orgName}: their{' '}
        <Anchor component={Link} to="/settings/themes">
          theme
        </Anchor>{' '}
        and their{' '}
        <Anchor component={Link} to="/settings/consent">
          consent notice
        </Anchor>
        .
      </Text>

      {!isOwner && <ReadOnlyNote what="settings" owner={orgName} />}
      {problem !== null && <ErrorNote message={problem} />}

      <Grid gap="xl">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="xl">
            <section>
              <Title order={2} size="h3" mb="xs">
                Name and websites
              </Title>
              <ReadEdit<Identity>
                noun="the name and websites"
                canEdit={isOwner}
                open={() => ({
                  name: brand.name,
                  domains: brand.domains.length > 0 ? [...brand.domains] : [''],
                })}
                check={(identity) => {
                  // Called from the press on Save, so this is an event rather than a render.
                  const found = identityProblems(identity);
                  setReveal(found.length > 0);
                  return found;
                }}
                save={(identity) =>
                  saveWith({
                    name: identity.name.trim(),
                    domains: tidyDomains(identity.domains),
                  })
                }
                form={(identity, set) => (
                  <Stack gap="lg">
                    <TextInput
                      id="brand-name"
                      label="Name"
                      description="What you call this masthead. Readers never see it unless your theme shows the credit."
                      value={identity.name}
                      maxLength={200}
                      onChange={(event) => set({ ...identity, name: event.currentTarget.value })}
                      style={{ maxWidth: '24rem' }}
                    />
                    <DomainsField
                      domains={identity.domains}
                      onChange={(domains) => set({ ...identity, domains })}
                      canEdit
                      serverProblem={domainProblem}
                      revealProblems={reveal}
                    />
                  </Stack>
                )}
              >
                <ReadList>
                  <ReadRow label="Name" value={brand.name} />
                  <ReadRow
                    label="Where this brand publishes"
                    value={
                      brand.domains.length === 0
                        ? 'No website yet, so nothing on this brand can be published'
                        : brand.domains.join(', ')
                    }
                    note="Only pages on these websites can post responses into this brand's results."
                  />
                </ReadList>
              </ReadEdit>
            </section>

            <section>
              <Title order={2} size="h3" mb="xs">
                Theme
              </Title>
              <ReadEdit<ThemeOverrides>
                noun="the theme"
                canEdit={isOwner}
                open={() => brand.theme ?? {}}
                check={(theme) => themeProblems(theme)}
                onDraft={setThemeDraft}
                save={(theme) => saveWith({ theme: isEmptyTheme(theme) ? null : theme })}
                form={(theme, set) => (
                  <ThemeSection
                    theme={theme}
                    onChange={set}
                    below={below}
                    system={orgView.system_defaults.theme}
                    canEdit
                    idPrefix="brand"
                  />
                )}
              >
                <ThemeRead
                  theme={brand.theme}
                  below={below}
                  system={orgView.system_defaults.theme}
                />
              </ReadEdit>
            </section>

            <section>
              <Title order={2} size="h3" mb="xs">
                Consent notice
              </Title>
              <ReadEdit<ConsentDraft>
                noun="the consent notice"
                canEdit={isOwner}
                open={() => ({
                  choice: consentChoiceOf(brand.consent_text),
                  text:
                    brand.consent_text !== null && brand.consent_text !== ''
                      ? brand.consent_text
                      : brand.resolved.consent_text,
                })}
                onDraft={setConsentDraft}
                save={(consent) =>
                  saveWith({
                    consent_text: consentPatchValue(consent.choice, consent.text.trim()),
                  })
                }
                form={(consent, set) => (
                  <ConsentSection
                    choice={consent.choice}
                    text={consent.text}
                    onChoice={(choice) => set({ ...consent, choice })}
                    onText={(text) => set({ ...consent, text })}
                    below={below}
                    system={orgView.system_defaults.consent_text}
                    canEdit
                    scope="brand"
                    idPrefix="brand"
                  />
                )}
              >
                <ConsentRead
                  value={brand.consent_text}
                  below={below}
                  system={orgView.system_defaults.consent_text}
                  scope="brand"
                />
                <SavedDifference text={savedDifference} />
              </ReadEdit>
            </section>

            <section>
              <Title order={2} size="h3" mb="xs">
                Newsletter signups
              </Title>
              <Text size="sm" mb="md">
                Two settings about the same thing, and neither is the notice above. That one
                explains what happens to a reader&apos;s answer. These are about the email address a
                reader hands over afterwards: what they are agreeing to receive, and what happens to
                the address once they have typed it.
              </Text>

              <Title order={3} size="h5" mb="xs">
                Newsletter wording
              </Title>
              <ReadEdit<ConsentDraft>
                noun="the newsletter wording"
                canEdit={isOwner}
                open={() => ({
                  choice: consentChoiceOf(brand.newsletter_consent_text ?? null),
                  text:
                    brand.newsletter_consent_text !== null &&
                    brand.newsletter_consent_text !== undefined &&
                    brand.newsletter_consent_text !== ''
                      ? brand.newsletter_consent_text
                      : newsletterInherited.value,
                })}
                save={(consent) =>
                  saveWith({
                    newsletter_consent_text: consentPatchValue(consent.choice, consent.text.trim()),
                  })
                }
                form={(consent, set) => (
                  <ConsentSection
                    choice={consent.choice}
                    text={consent.text}
                    onChoice={(choice) => set({ ...consent, choice })}
                    onText={(text) => set({ ...consent, text })}
                    below={below}
                    system={newsletterSystem}
                    canEdit
                    scope="brand"
                    subject="newsletter"
                    idPrefix="brand-newsletter"
                  />
                )}
              >
                <ConsentRead
                  value={brand.newsletter_consent_text ?? null}
                  below={below}
                  system={newsletterSystem}
                  scope="brand"
                  subject="newsletter"
                />
                <SavedDifference text={savedNewsletterDifference} />
              </ReadEdit>

              <Title order={3} size="h5" mt="lg" mb="xs">
                How a signup is taken
              </Title>
              <ReadEdit<NewsletterOptIn | null>
                noun="the opt in mode"
                canEdit={isOwner}
                open={() => brand.newsletter_opt_in ?? null}
                save={(optIn) => saveWith({ newsletter_opt_in: optIn })}
                form={(optIn, set) => (
                  <NewsletterSection
                    value={optIn}
                    inherited={inheritedMode}
                    onChange={set}
                    canEdit
                    scope="brand"
                    idPrefix="brand"
                  />
                )}
              >
                <NewsletterRead
                  value={brand.newsletter_opt_in ?? null}
                  inherited={inheritedMode}
                  scope="brand"
                />
              </ReadEdit>
            </section>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card withBorder radius="lg" p="md" style={{ position: 'sticky', top: 88 }}>
            <Title order={2} size="h4" mb="xs">
              What a reader sees
            </Title>
            <WidgetPreview theme={resolved.theme} segments={resolved.consent_segments} />
            <PreviewCaption />
          </Card>
        </Grid.Col>
      </Grid>

      <StaleList stale={brand.interactions_needing_republish} count={brand.interaction_count} />

      {isOwner && (
        <Card withBorder radius="lg" p="lg" mt="xl" component="section">
          <Title order={2} size="h3">
            Delete this brand
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            Only possible while it has nothing on it. Every response ever recorded carries the brand
            it came from, so a brand with interactions cannot be taken away from them.
          </Text>

          {deleteProblem !== null && <ErrorNote message={deleteProblem} />}

          {confirming ? (
            <>
              <Text size="sm" mt="md" fw={500}>
                Delete {brand.name}? This cannot be undone, and nothing can be published on it
                again.
              </Text>
              <Group mt="sm">
                <Button
                  color="red"
                  disabled={deleting}
                  onClick={() => {
                    if (orgId === null || brandId === undefined) return;
                    setDeleteProblem(null);
                    setDeleting(true);
                    api
                      .deleteBrand(orgId, brandId)
                      .then(() => {
                        notifications.show({ message: 'Brand deleted.', color: 'violet' });
                        void navigate('/settings/brands');
                      })
                      .catch((error: unknown) => {
                        setDeleteProblem(errorMessage(error));
                        setDeleting(false);
                      });
                  }}
                  autoFocus
                >
                  {deleting ? 'Deleting...' : `Yes, delete ${brand.name}`}
                </Button>
                <Button variant="default" onClick={() => setConfirming(false)}>
                  Keep it
                </Button>
              </Group>
            </>
          ) : (
            <Button
              variant="outline"
              color="red"
              mt="md"
              onClick={() => {
                setDeleteProblem(null);
                setConfirming(true);
              }}
            >
              Delete {brand.name}
            </Button>
          )}
        </Card>
      )}
    </>
  );
}

/**
 * A change to one brand: the fields one section owns, and no mention of the others.
 *
 * The two identity fields cannot be null. A brand with no name or no website is not a brand that
 * has gone back to inheriting something, it is a broken one, and the API refuses it.
 */
type BrandPatch = ConfigPatch & {
  readonly name?: string;
  readonly domains?: readonly string[];
  readonly newsletter_opt_in?: NewsletterOptIn | null;
};

/** What the name and websites form holds while it is open. */
interface Identity {
  readonly name: string;
  readonly domains: string[];
}

/** What the consent form holds while it is open: the choice and the words that go with it. */
interface ConsentDraft {
  readonly choice: ConsentChoice;
  readonly text: string;
}

/** Empty lines are how somebody adds a second website, so they are dropped rather than refused. */
function tidyDomains(domains: readonly string[]): string[] {
  return domains.map((entry) => entry.trim()).filter((entry) => entry !== '');
}

function identityProblems(identity: Identity): readonly string[] {
  const entries = tidyDomains(identity.domains);
  const found: string[] = [];
  if (identity.name.trim() === '') found.push('The brand needs a name, usually the masthead.');
  if (entries.length === 0) {
    found.push('A brand needs at least one website, or nothing on it can be published.');
  }
  for (const entry of entries) {
    const verdict = readDomain(entry);
    if (!verdict.ok) found.push(verdict.reason);
  }
  return found;
}

/**
 * What is live on this brand and still showing the notice it was published with.
 *
 * Publishing freezes the notice into the version, on purpose: it is a record of what a reader was
 * told rather than a setting. That is right and it is silent, so this is the list that says it out
 * loud. There is no bulk republish, so every one of them is a link to the screen where somebody can
 * read it and publish it again.
 */
function StaleList({
  stale,
  count,
}: {
  stale: readonly { interaction_id: string; title: string; live_version: number | null }[];
  count: number;
}) {
  if (stale.length === 0) {
    return (
      <Text c="dimmed" size="sm" mt="xl">
        {count === 0
          ? 'Nothing has been made on this brand yet.'
          : 'Everything live on this brand is showing the current consent notice.'}
      </Text>
    );
  }

  return (
    <Card withBorder radius="lg" p="lg" mt="xl" component="section">
      <Title order={2} size="h3">
        {stale.length === 1
          ? '1 live interaction is showing an older notice'
          : `${String(stale.length)} live interactions are showing an older notice`}
      </Title>
      <Text size="sm" mt="xs">
        A published version keeps the consent notice it went out with, because it is a record of
        what those readers were told. Until each of these is published again, readers see the older
        wording rather than the notice above. They have to be done one at a time.
      </Text>
      <List mt="md" spacing="xs">
        {stale.map((item) => (
          <List.Item key={item.interaction_id}>
            <Anchor component={Link} to={`/interactions/${item.interaction_id}`}>
              {item.title}
            </Anchor>
            {item.live_version !== null && (
              <Text component="span" size="sm" c="dimmed">
                {' '}
                (live on version {item.live_version})
              </Text>
            )}
          </List.Item>
        ))}
      </List>
    </Card>
  );
}

function BackLink() {
  return (
    <Text size="sm" mb="sm">
      <Anchor component={Link} to="/settings/brands" size="sm">
        Back to brands
      </Anchor>
    </Text>
  );
}
